import { Stack } from "@fluentui/react";
import React from "react";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import json from "react-syntax-highlighter/dist/esm/languages/hljs/json";
import { a11yLight } from "react-syntax-highlighter/dist/esm/styles/hljs";

import styles from "./AnalysisPanel.module.css";

import { Thoughts } from "../../api";
import { AgentPlan } from "./AgentPlan";
import { TokenUsageGraph } from "./TokenUsageGraph";

SyntaxHighlighter.registerLanguage("json", json);

interface Props {
    thoughts: Thoughts[];
    onCitationClicked?: (citationFilePath: string) => void;
}

// Helper to truncate URLs
function truncateImageUrl(val: string) {
    if (typeof val === "string" && val.startsWith("data:image/")) {
        return val.slice(0, 30) + "...";
    }
    return val;
}

type RetrievalTraceFact = {
    label: string;
    value: string;
    detail?: string;
};

type RetrievalTraceSummary = {
    facts: RetrievalTraceFact[];
    chips: string[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function getString(value: unknown): string | undefined {
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return undefined;
}

function getNumber(value: unknown): number | undefined {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) return Number(value);
    return undefined;
}

function formatSnakeLabel(value: string): string {
    return value
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^./, match => match.toUpperCase());
}

function formatCount(value: number | undefined, singular: string, plural = `${singular}s`): string | undefined {
    if (value === undefined) return undefined;
    return `${value} ${value === 1 ? singular : plural}`;
}

function getRetrievalTraceSummary(props: Thoughts["props"] | undefined): RetrievalTraceSummary | null {
    if (!isRecord(props)) return null;

    const scope = isRecord(props.selected_note_scope) ? props.selected_note_scope : null;
    const graphTrace = isRecord(props.graph_retrieval_trace) ? props.graph_retrieval_trace : null;
    const agenticGraph = isRecord(props.agentic_graph) ? props.agentic_graph : null;
    const facts: RetrievalTraceFact[] = [];
    const chips: string[] = [];

    if (scope) {
        const mode = getString(scope.mode);
        const selectedCount =
            getNumber(scope.selected_count) ?? getNumber(scope.note_count) ?? (Array.isArray(scope.note_ids) ? scope.note_ids.length : undefined);
        const validCount = getNumber(scope.valid_constrained_count) ?? getNumber(scope.constrained_count);
        const filteredOut = getNumber(scope.filtered_out_count);
        const value = mode === "all_current_browse_results" ? "All filtered Browse notes" : formatCount(selectedCount, "selected note") ?? "Selected Browse notes";
        const detailParts = [formatCount(validCount, "eligible note"), filteredOut !== undefined ? `${filteredOut} outside the active constraint` : undefined].filter(Boolean);
        facts.push({
            label: "Browse scope",
            value,
            detail: detailParts.length > 0 ? detailParts.join("; ") : "Scope metadata was included with the chat request."
        });
    }

    const retrievedCount = getNumber(props.actual_retrieved_count) ?? getNumber(props.retrieved_count) ?? getNumber(props.results_count);
    const citedCount = getNumber(props.cited_note_count) ?? getNumber(props.citation_count);
    if (retrievedCount !== undefined || citedCount !== undefined) {
        facts.push({
            label: "Notes surfaced",
            value: [formatCount(retrievedCount, "retrieved note"), formatCount(citedCount, "cited note")].filter(Boolean).join(" / "),
            detail: "Retrieved notes are candidates; cited notes are the subset used in the answer."
        });
    }

    if (agenticGraph) {
        const enabled = getString(agenticGraph.enabled);
        const stopReason = getString(agenticGraph.stop_reason) ?? getString(agenticGraph.reason);
        const rounds = getNumber(agenticGraph.rounds) ?? getNumber(agenticGraph.search_rounds);
        facts.push({
            label: "Agent path",
            value: enabled === "false" ? "Not used" : "Agentic retrieval",
            detail: [stopReason ? formatSnakeLabel(stopReason) : undefined, formatCount(rounds, "round")].filter(Boolean).join("; ") || "The retrieval planner exposed graph search diagnostics."
        });
    }

    if (graphTrace) {
        const recommendation = getString(graphTrace.recommendation) ?? getString(graphTrace.reason) ?? getString(graphTrace.stop_reason);
        const shared = getNumber(graphTrace.shared_count);
        const semanticOnly = getNumber(graphTrace.semantic_only_count);
        const graphOnly = getNumber(graphTrace.graph_only_count);
        facts.push({
            label: "Graph trace",
            value: recommendation ? formatSnakeLabel(recommendation) : "Available",
            detail: [
                shared !== undefined ? `${shared} shared` : undefined,
                semanticOnly !== undefined ? `${semanticOnly} semantic-only` : undefined,
                graphOnly !== undefined ? `${graphOnly} graph-only` : undefined
            ]
                .filter(Boolean)
                .join("; ")
        });
    }

    for (const key of ["retrieval_profile", "retrieval_effort", "retrieval_mode", "search_provider"]) {
        const value = getString(props[key]);
        if (value) chips.push(`${formatSnakeLabel(key)}: ${formatSnakeLabel(value)}`);
    }

    if (facts.length === 0 && chips.length === 0) return null;
    return { facts, chips };
}

export const ThoughtProcess = ({ thoughts, onCitationClicked }: Props) => {
    const [effort, setEffort] = React.useState<string | undefined>();

    return (
        <ul className={styles.tList}>
            {thoughts.map((t, ind) => {
                const hasAgenticPlan = Array.isArray(t.props?.query_plan) && t.props.query_plan.length > 0;
                const retrievalTraceSummary = getRetrievalTraceSummary(t.props);
                return (
                    <li className={styles.tListItem} key={ind}>
                        <div className={styles.tStep}>{t.title}</div>
                        <Stack horizontal tokens={{ childrenGap: 5 }} className={styles.tPropRow}>
                            {t.props &&
                                (Object.keys(t.props).filter(k => k !== "token_usage" && k !== "query_plan") || []).map((k: any) => (
                                    <span className={styles.tProp} key={k}>
                                        {k}: {truncateImageUrl(JSON.stringify(t.props?.[k]))}
                                    </span>
                                ))}
                            {hasAgenticPlan && effort && <span className={styles.tProp}>effort: {effort}</span>}
                        </Stack>
                        {retrievalTraceSummary && (
                            <div className={styles.traceSummary} aria-label="Why these notes">
                                <div className={styles.traceSummaryHeader}>Why These Notes?</div>
                                <div className={styles.traceSummaryGrid}>
                                    {retrievalTraceSummary.facts.map(fact => (
                                        <div key={fact.label} className={styles.traceSummaryItem}>
                                            <span>{fact.label}</span>
                                            <strong>{fact.value}</strong>
                                            {fact.detail && <small>{fact.detail}</small>}
                                        </div>
                                    ))}
                                </div>
                                {retrievalTraceSummary.chips.length > 0 && (
                                    <div className={styles.traceSummaryChips}>
                                        {retrievalTraceSummary.chips.map(chip => (
                                            <span key={chip}>{chip}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {t.props?.token_usage && !hasAgenticPlan && (
                            <TokenUsageGraph tokenUsage={t.props.token_usage} reasoningEffort={t.props.reasoning_effort} />
                        )}
                        {hasAgenticPlan && (
                            <AgentPlan
                                queryPlan={t.props?.query_plan ?? []}
                                onEffortExtracted={setEffort}
                                onCitationClicked={onCitationClicked}
                                results={Array.isArray(t.description) ? t.description : []}
                            />
                        )}
                        {Array.isArray(t.description) || (t.description !== null && typeof t.description === "object") ? (
                            <SyntaxHighlighter language="json" wrapLines wrapLongLines className={styles.tCodeBlock} style={a11yLight}>
                                {JSON.stringify(t.description, (key, value) => truncateImageUrl(value), 2)}
                            </SyntaxHighlighter>
                        ) : (
                            <div>{t.description}</div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};
